import { useMemo, useState } from 'react';
import { Drawer } from 'antd';
import { Link, useLocation, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { NAV } from '../app/nav';
import { activeNavRoute } from '../app/screens';
import { useAuth } from '../app/auth';
import { useUi } from '../app/ui';
import { visibleNav } from '../app/permissions';
import { NavIcon, ScreenIcon } from './NavIcon';
import { navItemLabel } from '../app/navAr';
import { roleNames } from '../app/roles';
import { NARROW_QUERY, useMediaQuery } from '../app/useMediaQuery';
import { SetPinModal } from './SetPinModal';

const DEFAULT_OPEN = 2; // first two groups open by default (mockup buildNav)

// Navy sidebar content: logo block, the nav groups the user may see (permissions.ts), user block with role names, Auth No,
// "Set signature PIN" and sign out.
export function SideNavPanel({ inDrawer }: { inDrawer?: boolean }) {
  const { t } = useTranslation();
  const lang = useUi((s) => s.lang);
  const closeSidebar = useUi((s) => s.closeSidebar);
  const user = useAuth((s) => s.user);
  const signOut = useAuth((s) => s.signOut);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [pinOpen, setPinOpen] = useState(false);

  const permissions = user?.permissions;
  const groups = useMemo(() => visibleNav(NAV, (code) => permissions?.includes(code) ?? false), [permissions]);

  const active = activeNavRoute(pathname);
  const activeGroup = groups.find((g) => g.items.some((i) => i.route === active))?.key;

  // manual toggles override the defaults; the active group is (re)opened whenever it changes — derived state, no effect
  const [manual, setManual] = useState<Record<string, boolean>>({});
  const [lastActive, setLastActive] = useState(activeGroup);
  if (activeGroup !== lastActive) {
    setLastActive(activeGroup);
    if (activeGroup) setManual((m) => ({ ...m, [activeGroup]: true }));
  }
  const isOpen = (key: string, index: number) => manual[key] ?? (index < DEFAULT_OPEN || key === activeGroup);

  const name = (lang === 'ar' && user?.displayNameAr) || user?.displayName || '';
  // the platform's user block leads with initials in a brand circle
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w.charAt(0).toUpperCase())
    .join('');

  return (
    <aside id="sidebar" className={inDrawer ? 'open' : undefined}>
      <div className="logo">
        <div className="emblem">{t('app.emblem')}</div>
        <div>
          <b>{t('app.name')}</b>
          <small id="logoSub">{t('app.section')}</small>
        </div>
      </div>
      <nav id="nav" aria-label={t('topbar.menu')}>
        {groups.map((g, index) => {
          const groupOpen = isOpen(g.key, index);
          return (
            <div key={g.key} className={`grp${groupOpen ? ' open' : ''}`}>
              <button
                type="button"
                aria-expanded={groupOpen}
                onClick={() => {
                  setManual((m) => ({ ...m, [g.key]: !groupOpen }));
                }}
              >
                <span className="lbl">
                  <NavIcon group={g.key} />
                  {lang === 'ar' ? g.labelAr : g.label}
                </span>
                <span className="car" aria-hidden="true">
                  ▸
                </span>
              </button>
              <ul>
                {g.items.map((it) => {
                  const isActive = it.route === active;
                  return (
                    <li key={it.route}>
                      <Link to={it.route} data-r={it.route.slice(1)} className={isActive ? 'active' : undefined} aria-current={isActive ? 'page' : undefined} onClick={closeSidebar}>
                        <ScreenIcon route={it.route} group={g.key} />
                        {navItemLabel(it.route, it.label, lang)}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </nav>
      <div className="user">
        <span className="avatar" aria-hidden="true">
          {initials || '·'}
        </span>
        <b id="uName">{name}</b>
        <span id="uRole">{roleNames(t, user?.roles ?? [])}</span>
        <br />
        <span className="small">
          {t('user.authNo', { no: user?.authNo ?? '—' })} ·{' '}
          <Link
            to="/login"
            id="setPin"
            onClick={(e) => {
              e.preventDefault();
              setPinOpen(true);
            }}
          >
            {t('user.setPin')}
          </Link>{' '}
          ·{' '}
          <Link
            to="/login"
            onClick={(e) => {
              e.preventDefault();
              void signOut().then(() => navigate('/login', { replace: true }));
            }}
          >
            {t('user.signOut')}
          </Link>
        </span>
      </div>
      <SetPinModal open={pinOpen} onClose={() => setPinOpen(false)} />
    </aside>
  );
}

// Desktop: the sidebar sits in the #app grid. Below 1000px it lives in an AntD Drawer opened from the top-bar hamburger (#menuBtn).
export function SideNav() {
  const narrow = useMediaQuery(NARROW_QUERY);
  const open = useUi((s) => s.sidebarOpen);
  const closeSidebar = useUi((s) => s.closeSidebar);
  const lang = useUi((s) => s.lang);
  const { t } = useTranslation();
  if (!narrow) return <SideNavPanel />;
  return (
    <Drawer open={open} onClose={closeSidebar} placement={lang === 'ar' ? 'right' : 'left'} width={248} closable={false} className="nav-drawer" title={null} aria-label={t('topbar.menu')}>
      <SideNavPanel inDrawer />
    </Drawer>
  );
}
